import { ConstraintClient } from '@frontend/constraint/api';
import { Constraint, ConstraintType, isStockConstraintData } from '@frontend/constraint/types';
import { CommonMessage } from '@frontend/lang';
import { SlotClient } from '@frontend/slot/api';
import { Slot } from '@frontend/slot/types';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';
import { TransactionLaundryExtendedClient, TransactionPudoExtendedClient, TransactionVendingClient } from '@frontend/transaction/api';
import { ExtendedLaundryTransaction, ExtendedTransaction, ExtendedVendingTransaction, TransactionStateName } from '@frontend/transaction/types';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { isMovableTransactionState, useMoveTransaction } from '../../hooks/useMoveTransaction';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { openSlot } from '../../utils/slot-opener';

export const formatDimension = (dim: { width: number; height: number; depth: number } | null | undefined): string => {
    if (!dim) return '/';
    return `${dim.width}mm × ${dim.height}mm × ${dim.depth}mm`;
};

export type SlotTransaction = ExtendedTransaction | ExtendedVendingTransaction | ExtendedLaundryTransaction;

export interface SlotDetailViewProps {
    slot: Slot | null;
    transactions: SlotTransaction[];
    stocks: StockExtended[];
    constraints: Constraint[];
    fetchTransactions: () => void;
    fetchStocks: () => void;
    isLoading: boolean;
    error: ReactNode | null;
    hasError: boolean;
    isOpeningSlot: boolean;
    handleOpenSlot: () => Promise<void>;
    canOpenSlot: boolean;
    canAddStock: boolean;
    canMoveTransaction: (transaction: SlotTransaction) => boolean;
    navigateToMove: (transaction: SlotTransaction) => void;
    updateStatusModalOpen: boolean;
    transactionForStatusModal: SlotTransaction | null;
    openUpdateStatusModal: (transaction: SlotTransaction) => void;
    closeUpdateStatusModal: () => void;
    handleUpdateStatusSuccess: () => void;
    navigate: ReturnType<typeof useKioskNavigate>;
    handleBack: () => void;
}

export const useSlotDetail = (): SlotDetailViewProps => {
    const { slotId } = useParams<{ slotId: string }>();
    const navigate = useKioskNavigate();
    const location = useLocation();
    const navigationState = useAppSelector((state) => state.navigation);
    const spotId = navigationState.spot?.id;
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const { canMove } = useMoveTransaction();

    const [slot, setSlot] = useState<Slot | null>(null);
    const [transactions, setTransactions] = useState<SlotTransaction[]>([]);
    const [stocks, setStocks] = useState<StockExtended[]>([]);
    const [constraints, setConstraints] = useState<Constraint[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);

    const [isOpeningSlot, setIsOpeningSlot] = useState(false);
    const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
    const [transactionForStatusModal, setTransactionForStatusModal] = useState<SlotTransaction | null>(null);

    const fetchTransactions = useCallback(() => {
        if (!slotId || !spotId) return Promise.resolve([] as SlotTransaction[]);
        return Promise.all([
            TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                spot_id: spotId,
                slot_id: slotId,
                active: 'true'
            })
                .then((r) => r.results ?? [])
                .catch(() => [] as SlotTransaction[]),
            TransactionLaundryExtendedClient.fetchExtendedLaundryTransactions({
                spot_id: spotId,
                slot_id: slotId,
                active: 'true'
            })
                .then((r) => r.results ?? [])
                .catch(() => [] as SlotTransaction[]),
            TransactionVendingClient.getVendingTransactions({
                spot_id: spotId,
                slot_id: slotId,
                active: 'true'
            })
                .then((r) => r.results ?? [])
                .catch(() => [] as SlotTransaction[])
        ]).then((results) => {
            const flat = results.flat();
            setTransactions(flat);
            return flat;
        });
    }, [slotId, spotId]);

    useEffect(() => {
        if (!slotId || !spotId || !accountId) {
            setIsLoading(false);
            if (!slotId) setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT));
            return;
        }

        setIsLoading(true);
        setError(null);

        SlotClient.fetchAccountSpotSlot(accountId, spotId, slotId)
            .then((slot) => {
                setSlot(slot);
                const { account_id, module_id } = slot;

                const stocksPromise = StockExtendedClient.fetchSpotSlotStocksExtended(account_id, spotId, module_id, slotId)
                    .then((r) => r.results)
                    .catch(() => [] as StockExtended[]);

                const constraintsPromise = ConstraintClient.fetchConstraints({
                    slot_id: slotId
                })
                    .then((r) => r.results)
                    .catch(() => [] as Constraint[]);

                return Promise.all([fetchTransactions(), stocksPromise, constraintsPromise]).then((results) => {
                    if (!results) return;
                    const [, stockList, constraintList] = results;
                    setStocks(stockList ?? []);
                    setConstraints(constraintList ?? []);
                });
            })
            .catch(() => {
                setError(CommonMessage.ERRORS.ENTITY_NOT_FOUND(CommonMessage.OBJECTS.SLOT.DEFAULT));
                setSlot(null);
            })
            .finally(() => setIsLoading(false));
    }, [slotId, spotId, accountId, fetchTransactions]);

    const fetchStocks = useCallback(() => {
        if (!slotId || !spotId || !accountId || !slot) return;
        StockExtendedClient.fetchSpotSlotStocksExtended(accountId, spotId, slot.module_id, slotId)
            .then((r) => setStocks(r.results ?? []))
            .catch(() => setStocks([]));
    }, [slotId, spotId, accountId, slot]);

    const handleOpenSlot = useCallback(async () => {
        if (!slot || !navigationState.iot) return;
        const { account_id, spot_id, module_id, id } = slot;
        setIsOpeningSlot(true);
        await openSlot({
            accountId: account_id,
            spotId: spot_id,
            moduleId: module_id,
            slotId: id,
            iotId: navigationState.iot.id
        });
        setIsOpeningSlot(false);
    }, [slot, navigationState.iot]);

    const openUpdateStatusModal = useCallback((tx: SlotTransaction) => {
        setTransactionForStatusModal(tx);
        setUpdateStatusModalOpen(true);
    }, []);

    const closeUpdateStatusModal = useCallback(() => {
        setUpdateStatusModalOpen(false);
        setTransactionForStatusModal(null);
    }, []);

    const handleUpdateStatusSuccess = useCallback(() => {
        fetchTransactions();
        setUpdateStatusModalOpen(false);
        setTransactionForStatusModal(null);
    }, [fetchTransactions]);

    const canMoveTransaction = useCallback(
        (transaction: SlotTransaction) => canMove && isMovableTransactionState(transaction.state as TransactionStateName),
        [canMove]
    );

    const navigateToMove = useCallback(
        (transaction: SlotTransaction) => {
            if (!slotId) return;
            navigate(`/spots/layout/slot/${slotId}/move/${transaction.id}`);
        },
        [navigate, slotId]
    );

    const handleBack = useCallback(() => {
        const state = location.state as { fromEquipmentAddFlow?: boolean } | null;
        if (state?.fromEquipmentAddFlow) {
            navigate(-3);
        } else {
            navigate(-1);
        }
    }, [location.state, navigate]);

    const hasError = !!error || !slot;
    const canOpenSlot = !!slot && !!navigationState.iot;

    // A slot can hold multiple stocks by default. A STOCK-type constraint with
    // `quantity === 1` caps the slot at a single stock (typically a dedicated
    // product slot), in which case adding is only allowed while the slot is
    // still empty.
    const canAddStock = useMemo(() => {
        const singleStockConstraint = constraints.some((c) => c.type === ConstraintType.STOCK && isStockConstraintData(c.data) && c.data.quantity === 1);
        return singleStockConstraint ? stocks.length === 0 : true;
    }, [constraints, stocks]);

    return {
        slot,
        transactions,
        stocks,
        constraints,
        fetchTransactions,
        fetchStocks,
        isLoading,
        error,
        hasError,
        isOpeningSlot,
        handleOpenSlot,
        canOpenSlot,
        canAddStock,
        canMoveTransaction,
        navigateToMove,
        updateStatusModalOpen,
        transactionForStatusModal,
        openUpdateStatusModal,
        closeUpdateStatusModal,
        handleUpdateStatusSuccess,
        navigate,
        handleBack
    };
};
